import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AppInformationService, AuthService, GenesisUnitOfWork, InvoiceService, ReportService, SessionInformationService, UtilityService } from 'app/core/core.module';
import { GridUtility } from 'app/core/grid-utility';
import { Order, OrderStatus, OrderType } from 'app/core/model/entity-model';
import { InputDialogComponent } from 'app/shared/input-dialog/input-dialog.component';
import { SharedModule } from 'app/shared/shared.module';
import { DxDataGridComponent } from 'devextreme-angular';
import { alert, confirm } from 'devextreme/ui/dialog';
import _ from 'lodash';
import moment from 'moment';
import { BsModalService } from 'ngx-bootstrap/modal';

@Component({
    selector: 'app-order-search',
    templateUrl: './order-search.component.html',
    styleUrls: ['./order-search.component.scss'],
    standalone: false,
})
export class OrderSearchComponent implements OnInit {
    invoiceService = inject(InvoiceService);

    orders: any;
    showAllOrders = false;
    filter: any;
    orderStatuses: OrderStatus[];
    canUpdateStatus = false;
    canCancelOrder = false;
    
    isQuotesMode = false;

    @ViewChild('dataGrid') dataGrid: DxDataGridComponent;
    gridUtil: GridUtility;
    onExporting = SharedModule.onExporting;

    constructor(
        private uow: GenesisUnitOfWork,
        private router: Router,
        private route: ActivatedRoute,
        public auth: AuthService,
        private appInfo: AppInformationService,
        private reportService: ReportService,
        private utility: UtilityService,
        private modalService: BsModalService,
        private sessionInfo: SessionInformationService
    ) {}

    async ngOnInit() {
        this.isQuotesMode = this.route.snapshot.data.title === 'Quotes';
        const deliveryOptions = this.uow.lookups.deliveryOptions;
        this.orderStatuses = this.uow.lookups.orderStatuses;

        this.filter = this.sessionInfo.getItem('order-search-filter') ?? { showCompleted: false };
        await this.getData();
        this.gridUtil = new GridUtility(this.dataGrid, 'order-search-grid', this.sessionInfo);
        this.gridUtil.setScrollPosition();

        this.canUpdateStatus = this.auth.hasRole('SalesOrder:UpdateStatus');
        this.canCancelOrder = this.auth.hasRole('SalesOrder:CancelOrder');

        this.utility.completeOrders();
    }

    async getData() {
        let orders = null;
        const dealers = await this.uow.getDealers(true);
        orders = (await this.uow.getOrders('salesRep', this.filter.showCompleted)) as Order[];
        if (this.isQuotesMode) {
            orders = orders.filter(x => x.typeId === OrderType.Types.Quote)
        }
        this.orders = orders;
    }

    async toggleCompleted() {
        this.filter.showCompleted = !this.filter.showCompleted;
        this.sessionInfo.setItem('order-search-filter', this.filter);
        await this.getData();
    }

    async print() {
        const selectedInvoices = this.dataGrid.instance.getSelectedRowsData();
        if (selectedInvoices.length === 0) return;
        this.appInfo.pleaseWaitShow();
        selectedInvoices.forEach((x) => {
            if (x.orderGuid == null) x.orderGuid = crypto.randomUUID();
        });
        await this.uow.commit();
        const orderGuids = selectedInvoices.map((x) => x.orderGuid);
        this.reportService.openSalesReport(orderGuids);
        this.appInfo.pleaseWaitHide();
    }

    search() {}
    clear() {}

    // this method is used to clear the selection when the user searches for something
    onOptionChanged(e) {
        if (e.fullName === 'searchPanel.text') {
            e.component.__searchChanged = true;
        }
        const filterCheck = _.includes(e.fullName, 'filterValue');
        if (filterCheck) {
            this.dataGrid.instance.clearSelection();
        }
    }

    // this method is used to clear the selection when the user searches for something
    onContentReady(e) {
        if (e.component.__searchChanged) {
            //__searchChanged is for the search box
            e.component.__searchChanged = false;
            this.dataGrid.instance.clearSelection();
        }
    }

    async rowPrepare(e) {
        if (e.rowType !== 'data') return;

        const order = e.data;
        if (order.typeId === OrderType.Types.Quote) {
            e.rowElement.className = e.rowElement.className + ' quote';
        } else if (order.typeId === Order.Types.Warranty) {
            e.rowElement.className = e.rowElement.className + ' warranty';
        } else if (order.typeId === Order.Types.RetailPartsOrder) {
            e.rowElement.className = e.rowElement.className + ' retail-parts';
        } else if (order.typeId === Order.Types.SalesOrder) {
            if (order.statusId === Order.Statuses.New) e.rowElement.className = e.rowElement.className + ' sales-order';
            if (order.statusId === Order.Statuses.WaitingOnConfirmation) e.rowElement.className = e.rowElement.className + ' waiting-on-dealer-confirmation';
        }
    }

    selectionChange(e) {}

    onToolbarPreparing(e) {
        e.toolbarOptions.items.unshift(
            {
                location: 'before',
                template: 'toolbarButtons',
            },
            {
                template: 'toolbarText',
                location: 'after',
            },
            {
                widget: 'dxButton',
                options: {
                    icon: 'revert',
                    elementAttr: { title: 'Reset Filters' },
                    onClick: () => {
                        e.component.clearFilter();
                    },
                },
                location: 'after',
            },
            {
                widget: 'dxButton',
                options: {
                    icon: 'refresh',
                    elementAttr: { title: 'Reset Grid Layout' },
                    onClick: () => {
                        e.component.state({});
                    },
                },
                location: 'after',
            }
        );
    }

    async open(order: any) {
        const queryParams = this.isQuotesMode ? { quote: this.isQuotesMode } : {};
        this.router.navigate(['/sales-orders/' + (!!order?.id ? order.id : 'new')], { queryParams });
    }

    async rowDoubleClick(e) {
        if (!e.data || !e.data.id) return;
        await this.open(e.data);
    }

    async reKickOff(item) {
        console.log(item);
        const order = item[0];
        if (order.statusId <= 1) {
            alert(`Can Re-Kickoff a on order with New status.`, 'Error');
            return;
        }
        const result = await (confirm(
            `Are you sure you want to Re-Kickoff this order? This will delete all parts and recreate them.`,
            `${this.appInfo.settings.company}`
        ) as Promise<boolean>);
        if (!result) return;
        const orderLineDetails = (await this.uow.getOrderBuildDetailByOrderId(order.id)) as any;
        if (orderLineDetails.length === 0) {
            alert(`Cannot kickoff order no order line details were found.`, 'Error');
            return;
        }
        this.utility.processOrderLineDetails(orderLineDetails.map((x) => x.id));
    }

    async updateStatus() {
        const selectedOrders = this.dataGrid.instance.getSelectedRowsData();
        const orderIds = selectedOrders.map((x) => {
            return x.id;
        });
        const buildDetails = (await this.uow.getBuildDetailsByOrderIds(orderIds)) as any;
        if (buildDetails.length > 0) {
            const result = await confirm('There are Build Details for one or more selected rows.  Do you want to continue anyway?', `${this.appInfo.settings.company}`);
            if (!result) return;
        }

        const newStatus = await this.getSelectedStatus();
        if (!newStatus) return;

        selectedOrders.forEach((order) => {
            order.statusId = newStatus;
        });
        await this.uow.commit();
        await this.getData();
    }

    async getSelectedStatus(): Promise<number> {
        const status = this.uow.lookups.orderStatuses;

        const promise = await new Promise<number | null>(async (resolve) => {
            const modalRef = this.modalService.show(InputDialogComponent, {
                class: 'modal-sm',
                ignoreBackdropClick: true,
                keyboard: false,
                initialState: {
                    dialogHeader: 'Update Order Status',
                    dialogPrompt: 'Select Status',
                    mode: 'selectWithId',
                    dialogValueArray: this.orderStatuses,
                },
            });
            modalRef.content.result.subscribe(async (result) => {
                if (result.result && result.value) resolve(result.value);
                modalRef.hide();
                resolve(null);
            });
        });
        return promise;
    }

    async cancelOrder() {
        const selectedOrders = this.dataGrid.instance.getSelectedRowsData();
        if (!selectedOrders) return;

        this.appInfo.pleaseWaitShow();
        const orderIds = selectedOrders.map((x) => {
            return x.id;
        });
        const buildDetails = (await this.uow.getBuildDetailsByOrderIds(orderIds)) as any;
        if (buildDetails.length > 0) {
            this.appInfo.pleaseWaitHide();
            await alert('One or more of the selected orders has a Build Detail.  Cannot Cancel.', `${this.appInfo.settings.company}`);
            return;
        }
        const wrongStatus = selectedOrders.filter((x) => {
            return x.statusId !== Order.Statuses.WaitingOnConfirmation && x.statusId !== Order.Statuses.New;
        }).length;
        if (wrongStatus > 0) {
            this.appInfo.pleaseWaitHide();
            await alert('One or more of the selected orders is not on the Waiting for Confirmation status.  Cannot Cancel.', `${this.appInfo.settings.company}`);
            return;
        }

        selectedOrders.forEach((order) => {
            order.statusId = Order.Statuses.Canceled;
            order.note = `Order Canceled by ${this.auth.currentUser.userName} on ${moment(new Date()).format('MM/DD/YYYY hh:mm:ssa')}`;
        });
        await this.uow.commit();
        await this.getData();
        this.appInfo.pleaseWaitHide();
    }

    async createInvoice() {
        const selectedRows = this.dataGrid.instance.getSelectedRowsData();
        if (!selectedRows) return;

        const dealer = selectedRows[0].dealer;
        const areSameDealer = selectedRows.every((order) => dealer.id === order.dealerId);
        if (!areSameDealer) {
            await alert('Cannot create invoice for orders for different dealers', this.appInfo.settings.company);
            return;
        }

        const areInProcessOrComplete = selectedRows.every((order) => order.statusId === 2 || order.statusId === 3);
        if (!areInProcessOrComplete) {
            await alert('Cannot create invoice for orders that are not submitted', this.appInfo.settings.company);
            return;
        }

        const orderIds = selectedRows.map((order) => order.id);
        this.invoiceService.createInvoice(orderIds, 'salesOrder');
    }

    async markAsPaid(orders: Order[]) {}
}
